import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { DEMAND_TYPE_CONFIG } from './useSupportInbox';
import type { DemandType } from '@/types/support.types';
import type { Database } from '@/integrations/supabase/types';

type DbSessionStatus = Database['public']['Enums']['support_session_status'];
type DbResolutionType = Database['public']['Enums']['resolution_type'];

interface AssociatePayload {
  preTicketId: string;
  targetSessionId: string;
  contactId: string;
}

export function useAssociatePreTicket() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ preTicketId, targetSessionId, contactId }: AssociatePayload) => {
      // 1. Detect interaction type
      const { data: interactionType } = await supabase.rpc('detect_interaction_type', {
        p_session_id: targetSessionId,
        p_message_text: 'Mensagem associada de pré-chamado',
      });

      const type = interactionType || 'response';

      // 2. Create interaction on target session
      const { error: interactionError } = await supabase
        .from('support_interactions')
        .insert({
          session_id: targetSessionId,
          contact_id: contactId,
          type: type as any,
          notes: 'Mensagem associada de pré-chamado',
          auto_detected: true,
          detection_reason: `Pre-ticket associated, detected as ${type}`,
        });

      if (interactionError) throw interactionError;

      // 3. Update target session timestamps
      const updates: Record<string, any> = {
        last_client_message_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (type === 'follow_up') {
        // Increment follow_up_count via raw update
        const { data: targetSession } = await supabase
          .from('support_sessions')
          .select('follow_up_count, status')
          .eq('id', targetSessionId)
          .single();

        updates.follow_up_count = (targetSession?.follow_up_count || 0) + 1;
      }

      if (type === 'reopened') {
        const { data: targetSession } = await supabase
          .from('support_sessions')
          .select('reopen_count')
          .eq('id', targetSessionId)
          .single();

        updates.status = 'in_progress' as DbSessionStatus;
        updates.reopen_count = (targetSession?.reopen_count || 0) + 1;
      }

      const { error: updateError } = await supabase
        .from('support_sessions')
        .update(updates)
        .eq('id', targetSessionId);

      if (updateError) throw updateError;

      // 4. Close pre-ticket
      const { error: closeError } = await supabase
        .from('support_sessions')
        .update({
          status: 'auto_archived' as DbSessionStatus,
          resolution_type: 'duplicate' as DbResolutionType,
          resolved_at: new Date().toISOString(),
          resolution_notes: `Associado ao chamado ${targetSessionId}`,
        })
        .eq('id', preTicketId);

      if (closeError) throw closeError;

      // 5. Re-link messages from pre-ticket to target session
      const { error: relinkError } = await supabase
        .from('messages')
        .update({ owner_session_id: targetSessionId })
        .eq('owner_session_id', preTicketId);

      if (relinkError) console.error('Failed to relink messages:', relinkError);

      return { targetSessionId, type };
    },
    onSuccess: ({ targetSessionId, type }) => {
      // Get target session demand type for toast
      queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
      queryClient.invalidateQueries({ queryKey: ['contact-sessions'] });
      toast.success('Mensagem associada ao chamado');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao associar pré-chamado');
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async ({ preTicketId }: { preTicketId: string }) => {
      const { error } = await supabase
        .from('support_sessions')
        .update({
          status: 'auto_archived' as DbSessionStatus,
          resolution_type: 'resolved' as DbResolutionType,
          resolved_at: new Date().toISOString(),
          resolution_notes: 'Encerrado sem abertura de chamado',
        })
        .eq('id', preTicketId);

      if (error) throw error;
      return { preTicketId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
      queryClient.invalidateQueries({ queryKey: ['contact-sessions'] });
      toast.success('Pré-chamado encerrado');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao encerrar pré-chamado');
    },
  });

  return {
    associate: mutation.mutateAsync,
    isAssociating: mutation.isPending,
    resolvePreTicket: resolveMutation.mutateAsync,
    isResolving: resolveMutation.isPending,
  };
}
